import PropTypes from 'prop-types';
import useReveal from '../../hooks/useReveal';
import styles from './reveal.module.scss';

/*
Reveal: wraps content in an element that animates into view on scroll.

Uses the `useReveal` IntersectionObserver hook and CSS transitions only — no
animation library. Honors prefers-reduced-motion via the stylesheet.

Pass `enabled={false}` to render the plain element with no animation — handy
for gating the entrance to a single theme (e.g. `enabled={isEggshell}`) while
leaving other usages of a shared component visually unchanged.

@prop {string} variant - entrance direction: 'up' | 'down' | 'left' | 'right' | 'fade' | 'scale'
@prop {number} delay - stagger delay in ms (applied as transition-delay)
@prop {string} as - element/tag to render (default 'div')
@prop {string} className - extra class names passed through to the wrapper
@prop {boolean} once - reveal only the first time it enters view (default true)
@prop {boolean} enabled - when false, render children without any animation (default true)
*/
export default function Reveal({
  children,
  variant = 'up',
  delay = 0,
  as: Tag = 'div',
  className = '',
  once = true,
  enabled = true,
  ...rest
}) {
  // Hook is always called (rules of hooks); its result is simply ignored when
  // the animation is disabled.
  const [ref, inView] = useReveal({ once });

  if (!enabled) {
    return (
      <Tag className={className} {...rest}>
        {children}
      </Tag>
    );
  }

  const classes = [
    styles.reveal,
    styles[variant] || styles.up,
    inView ? styles.inView : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag
      ref={ref}
      className={classes}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

Reveal.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['up', 'down', 'left', 'right', 'fade', 'scale']),
  delay: PropTypes.number,
  as: PropTypes.elementType,
  className: PropTypes.string,
  once: PropTypes.bool,
  enabled: PropTypes.bool,
};
